import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { get2faStatus, enable2faInit, enable2faConfirm, disable2fa } from '../lib/api';
import { ShieldCheck, ShieldWarning, LockKey, Copy, CheckCircle, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function TwoFactorAuth() {
  const [status, setStatus] = useState(null);
  const [initData, setInitData] = useState(null);
  const [enableCode, setEnableCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableDialog, setDisableDialog] = useState(false);
  const [disablePwd, setDisablePwd] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await get2faStatus();
      setStatus(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchStatus(); }, []);

  const startEnable = async () => {
    setLoading(true);
    try {
      const r = await enable2faInit();
      setInitData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const confirmEnable = async () => {
    if (enableCode.length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const r = await enable2faConfirm(enableCode);
      setRecoveryCodes(r.data.recovery_codes);
      setInitData(null);
      setEnableCode('');
      fetchStatus();
      toast.success('2FA enabled successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const confirmDisable = async () => {
    setLoading(true);
    try {
      await disable2fa(disablePwd, disableCode);
      toast.success('2FA disabled');
      setDisableDialog(false);
      setDisablePwd('');
      setDisableCode('');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery codes copied');
  };

  return (
    <Layout>
      <div data-testid="2fa-page" className="p-6 max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <LockKey size={32} weight="fill" className="text-brand" />
            Two-Factor Authentication
          </h1>
          <p className="text-muted-foreground">Secure your account with a time-based code from an authenticator app</p>
        </div>

        {/* Status card */}
        {status && (
          <div className={`border-2 p-5 mb-4 ${status.enabled ? 'bg-green-50 border-green-500' : 'bg-amber-50 border-amber-500'}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {status.enabled ? (
                  <ShieldCheck size={40} weight="fill" className="text-green-700" />
                ) : (
                  <ShieldWarning size={40} weight="fill" className="text-amber-700" />
                )}
                <div>
                  <p className="overline">Current Status</p>
                  <p className="text-2xl font-bold" data-testid="2fa-status">
                    {status.enabled ? '2FA Enabled' : '2FA Not Configured'}
                  </p>
                  {status.enabled && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {status.recovery_codes_remaining} recovery codes remaining
                    </p>
                  )}
                </div>
              </div>
              {status.enabled ? (
                <Button
                  variant="outline"
                  onClick={() => setDisableDialog(true)}
                  data-testid="2fa-disable-btn"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  Disable 2FA
                </Button>
              ) : (
                <Button
                  onClick={startEnable}
                  disabled={loading || !!initData}
                  data-testid="2fa-enable-btn"
                  className="bg-brand hover:bg-brand/90"
                >
                  Enable 2FA
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Enable flow: QR + confirm */}
        {initData && (
          <div className="border border-border bg-card p-5" data-testid="2fa-enable-flow">
            <h2 className="font-bold text-lg mb-4">Set up authenticator</h2>
            <ol className="space-y-4">
              <li>
                <p className="font-bold mb-1">1. Scan this QR code in Google Authenticator, Authy, or 1Password:</p>
                <div className="flex gap-4 items-start flex-wrap">
                  <img
                    src={initData.qr_code_data_url}
                    alt="2FA QR Code"
                    className="border border-border w-48 h-48"
                    data-testid="2fa-qr-code"
                  />
                  <div className="bg-muted border border-border p-3 text-xs flex-1 min-w-0">
                    <p className="overline mb-1">Or enter manually</p>
                    <p className="font-mono text-xs break-all select-all" data-testid="2fa-secret">{initData.secret}</p>
                    <p className="text-muted-foreground mt-2 text-xs">Account: {initData.account_name}</p>
                    <p className="text-muted-foreground text-xs">Issuer: {initData.issuer}</p>
                  </div>
                </div>
              </li>
              <li>
                <p className="font-bold mb-1">2. Enter the 6-digit code from your app:</p>
                <div className="flex gap-2 items-center">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={enableCode}
                    onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, ''))}
                    className="font-mono text-2xl text-center w-40 tracking-widest"
                    data-testid="2fa-confirm-code"
                  />
                  <Button
                    onClick={confirmEnable}
                    disabled={loading || enableCode.length !== 6}
                    className="bg-brand hover:bg-brand/90"
                    data-testid="2fa-confirm-btn"
                  >
                    Verify &amp; Enable
                  </Button>
                  <Button variant="outline" onClick={() => { setInitData(null); setEnableCode(''); }}>
                    Cancel
                  </Button>
                </div>
              </li>
            </ol>
          </div>
        )}

        {/* Recovery codes dialog */}
        <Dialog open={!!recoveryCodes} onOpenChange={(o) => !o && setRecoveryCodes(null)}>
          <DialogContent data-testid="recovery-codes-dialog" className="border border-border rounded-none">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-green-600" />
                2FA Enabled · Save Your Recovery Codes
              </DialogTitle>
            </DialogHeader>
            <div className="p-1 bg-amber-50 border border-amber-200 text-xs text-amber-900 mb-3">
              <Warning size={14} weight="fill" className="inline mr-1" />
              These codes are shown <span className="font-bold">once</span>. Save them securely. Each can be used once if you lose your authenticator.
            </div>
            {recoveryCodes && (
              <div className="bg-muted border border-border p-4 font-mono text-sm grid grid-cols-2 gap-2" data-testid="recovery-codes-list">
                {recoveryCodes.map((c, i) => (
                  <div key={i} className="select-all" data-testid={`recovery-code-${i}`}>{c}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Button onClick={copyRecoveryCodes} variant="outline" className="flex-1" data-testid="copy-recovery-btn">
                <Copy size={14} className="mr-2" /> Copy All
              </Button>
              <Button onClick={() => setRecoveryCodes(null)} className="flex-1 bg-brand hover:bg-brand/90" data-testid="recovery-close-btn">
                I've Saved Them
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Disable dialog */}
        <Dialog open={disableDialog} onOpenChange={setDisableDialog}>
          <DialogContent data-testid="2fa-disable-dialog" className="border border-border rounded-none">
            <DialogHeader>
              <DialogTitle>Disable Two-Factor Auth</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="p-2 bg-red-50 border border-red-200 text-xs text-red-800">
                <Warning size={14} weight="fill" className="inline mr-1" />
                This will reduce your account security. You'll need your current password + a valid 2FA code.
              </div>
              <div>
                <Label className="overline">Current Password</Label>
                <Input
                  type="password"
                  value={disablePwd}
                  onChange={(e) => setDisablePwd(e.target.value)}
                  className="mt-1"
                  data-testid="2fa-disable-pwd"
                />
              </div>
              <div>
                <Label className="overline">Current 6-digit code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  inputMode="numeric"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1 font-mono"
                  data-testid="2fa-disable-code"
                />
              </div>
              <Button
                onClick={confirmDisable}
                disabled={loading || !disablePwd || disableCode.length !== 6}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="2fa-disable-confirm-btn"
              >
                Disable 2FA
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
